import { Plug } from 'lucide-react'
import {
  ConfirmDialog,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from 'components'
import type { useGatewayConnect } from '../hooks/use-gateway-connect'

type GatewayConnect = ReturnType<typeof useGatewayConnect>

/**
 * The dialogs a partner-gateway (Composio/Phyllo) connect can need after its
 * link step: the Page picker, and the Instagram "switch account?" confirm when
 * select-account returns 409. Shared by the Integrations page and the popup
 * that finishes a connect started from any other surface.
 */
export function GatewayConnectDialogs({ connect }: { connect: GatewayConnect }) {
  const { picker, selecting, selectPage, closePicker, pendingMismatch, confirmMismatch } = connect
  return (
    <>
      <Dialog open={!!picker} onOpenChange={open => !open && closePicker()}>
        <DialogContent variant="dark" size="sm">
          <DialogHeader>
            <DialogTitle variant="dark">Choose a Page</DialogTitle>
            <DialogDescription variant="dark">
              Select which account Taka should publish to.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-2 space-y-2">
            {picker?.pages.length === 0 && (
              <p className="text-sm text-paper-dim">
                No Pages were found for this connection. Try connecting again.
              </p>
            )}
            {picker?.pages.map(page => (
              <button
                key={page.id}
                type="button"
                disabled={selecting}
                onClick={() => selectPage(page.id)}
                className="flex w-full items-center gap-3 rounded-[10px] border border-line bg-bg-3 p-3 text-left transition-colors hover:border-line-bright disabled:opacity-50"
              >
                <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center overflow-hidden rounded-lg bg-bg-2">
                  {page.picture ? (
                    <img src={page.picture} alt={page.name} className="h-9 w-9 object-cover" />
                  ) : (
                    <Plug size={18} className="text-paper-dim" />
                  )}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-paper">{page.name}</p>
                  {page.handle && <p className="text-xs text-paper-dim">@{page.handle}</p>}
                </div>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Instagram select-account returned 409 (account-match guard): the
          newly authorized IG account differs from the previously connected
          one. Confirm to re-point publishing and automations to the new
          handle; cancel to bail. The hook holds the retry context. */}
      <ConfirmDialog
        open={pendingMismatch !== null}
        onOpenChange={open => {
          if (!open) connect.cancelMismatch()
        }}
        title="Switch Instagram account?"
        description={
          pendingMismatch
            ? `You connected @${pendingMismatch.newHandle}, but this business was previously linked to @${pendingMismatch.previousHandle}. Switching will re-point publishing and automations to @${pendingMismatch.newHandle}.`
            : ''
        }
        confirmLabel="Switch account"
        pendingLabel="Switching…"
        onConfirm={confirmMismatch}
      />
    </>
  )
}
